import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { get } from 'lodash';
import { TbCarOff, TbTrendingDown } from 'react-icons/tb';
import { MdOutlineCreditCardOff } from 'react-icons/md';
import { PiSortAscending } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FaMobileScreenButton } from 'react-icons/fa6';
import { IoMdNotifications } from 'react-icons/io';
import ComponentCard from '../../common/components/cards/ComponentCard';
import Chart from './charts/Chart';
import { getColumnOptions } from './charts/columnChartOptions';
import { getDonutOptions } from './charts/donutChartOptions';
import DetailedSnapshotCard from './DetailedSnapshotCard';
import { capitalizeString } from '../../helpers/capitalizeString';
import { getDateDaysAgo, getTodayDate } from '../../common/utils/date/Date.utils';
import { fetchCustomerStatistics } from '../DashboardActions';
import { getHorizontalBarChartOptions } from './charts/horizontalBarChartOptions';
import { SET_DEALER_SEARCH_STATE } from '../../dealers/DealersReducer';
import RouterPaths from '../../constants/RouterPaths';
import { getMixedChartOptions } from './charts/mixedChartOptions';
import { getRangeColumnCharts } from './charts/rangeColumnChartOptions';
import useWindowSize from '../../common/hooks/useWindowSize';

function CustomerOverviewSection() {
  const navigate = useNavigate();
  const customerStatistics = useSelector((state) => state.dashboard.get('customerStatistics'));
  const feesStatistics = useSelector((state) => state.dashboard.get('feesStatistics'));

  const windowSize = useWindowSize(0);

  const [feesChartsAlignedHorizontally, setFeesChartsAlignedHorizontally] = useState(true);

  useEffect(() => {
    setFeesChartsAlignedHorizontally(windowSize.width >= 1350);
  }, [windowSize]);

  const mapFreeTrialsSeries = () => {
    if (!customerStatistics) return [];
    const { freeTrialUserPercentage } = customerStatistics;
    if (!freeTrialUserPercentage) return [];
    return [freeTrialUserPercentage, 100 - freeTrialUserPercentage];
  };

  const mapWeeklySignUpsSeries = () => {
    if (!customerStatistics) return [];
    const { usersWeekDayCount } = customerStatistics;
    const subscribersData = [];
    const buyersData = [];
    const sellersData = [];

    usersWeekDayCount.forEach((it) => {
      subscribersData.push(it.subscribers);
      buyersData.push(it.buyers);
      sellersData.push(it.sellers);
    });

    return [
      {
        name: 'Subscribers',
        data: subscribersData,
      },
      {
        name: 'Buyers',
        data: buyersData,
      },
      {
        name: 'Sellers',
        data: sellersData,
      },
    ];
  };

  const mapCitiesData = () => {
    if (!customerStatistics) return { labels: [], series: [] };

    const topCities = customerStatistics?.topCities ?? [];
    const labels = topCities.map((it) => capitalizeString(it.city));
    const counts = topCities.map((it) => it.count);

    return {
      labels,
      series: [
        {
          name: 'Count',
          data: counts,
        },
      ],
    };
  };

  const mapSalesVersusCapData = () => {
    const { salesVersusCapByMonth } = feesStatistics;

    const columnData = salesVersusCapByMonth.map((it) => it.sales);
    const lineData = salesVersusCapByMonth.map((it) => it.cap);
    const labels = salesVersusCapByMonth.map((it) => getMonthName(it.month - 1));

    return {
      series: [
        {
          name: 'Sales',
          type: 'column',
          data: columnData,
        },
        {
          name: 'Cap',
          type: 'line',
          data: lineData,
        },
      ],
      labels,
    };
  };

  const getMonthName = (number) => moment().month(number).format('MMM').slice(0, 3);

  const mapBuyersAndSellersFees = () => {
    const buyersFees = [];
    const sellersFees = [];

    const { feesByMonth } = feesStatistics;
    feesByMonth.forEach((it) => {
      buyersFees.push({
        x: getMonthName(it.month - 1),
        y: [it.buyersFees],
      });
      sellersFees.push({
        x: getMonthName(it.month - 1),
        y: [it.sellersFees],
      });
    });

    return [
      {
        name: 'Buyers Fees',
        data: buyersFees,
      },
      {
        name: 'Sellers Fees',
        data: sellersFees,
      },
    ];
  };

  const mapMonthlySignUpsSeries = () => {
    if (!customerStatistics) return [];
    const { usersMonthCount } = customerStatistics;
    const subscribersData = [];
    const buyersData = [];
    const sellersData = [];

    usersMonthCount.forEach((it) => {
      const { subscribers, sellers, buyers } = it;
      subscribersData.push(subscribers);
      buyersData.push(buyers);
      sellersData.push(sellers);
    });

    return [
      {
        name: 'Subscribers',
        data: subscribersData,
      },
      {
        name: 'Buyers',
        data: buyersData,
      },
      {
        name: 'Sellers',
        data: sellersData,
      },
    ];
  };

  const today = getTodayDate();
  const thirtyDaysAgo = getDateDaysAgo(30);

  const dispatch = useDispatch();

  const [dataInitialized, setDataInitialized] = useState(false);
  const [buyersWithMaxOneBoughtVehicleRangeStart, setBuyersWithMaxOneBoughtVehicleRangeStart] =
    useState(thirtyDaysAgo);

  const [buyersWithMaxOneBoughtVehicleRangeEnd, setBuyersWithMaxOneBoughtVehicleRangeEnd] =
    useState(today);

  const [sellersWithMaxOneSoldVehicleRangeStart, setSellersWithMaxOneSoldVehicleRangeStart] =
    useState(thirtyDaysAgo);
  const [sellersWithMaxOneSoldVehicleRangeEnd, setSellersWithMaxOneSoldVehicleRangeEnd] =
    useState(today);

  const freeTrialsSeries = mapFreeTrialsSeries();
  const weeklySignUpsSeries = mapWeeklySignUpsSeries();
  const monthlySignUpsSeries = mapMonthlySignUpsSeries();
  const citiesData = mapCitiesData();
  const salesVersusCapData = mapSalesVersusCapData();

  useEffect(() => {
    if (!dataInitialized) {
      setDataInitialized(true);
      return;
    }
    dispatch(
      fetchCustomerStatistics({
        buyersWithMaxOneBoughtVehicleRangeStart,
        buyersWithMaxOneBoughtVehicleRangeEnd,
        sellersWithMaxOneSoldVehicleRangeStart,
        sellersWithMaxOneSoldVehicleRangeEnd,
      }),
    );
  }, [
    buyersWithMaxOneBoughtVehicleRangeStart,
    buyersWithMaxOneBoughtVehicleRangeEnd,
    sellersWithMaxOneSoldVehicleRangeStart,
    sellersWithMaxOneSoldVehicleRangeEnd,
  ]);

  const handleCityChartBarClick = (value) => {
    if (!value) return;
    dispatch({
      type: SET_DEALER_SEARCH_STATE,
      payload: {
        searchParams: {
          offset: 0,
          city: value,
        },
      },
    });
    navigate(RouterPaths.DEALERS);
  };

  const handleGoToLocations = () => {
    navigate(RouterPaths.LOCATIONS);
  };

  return (
    <>
      <Row>
        <Col xs={12} md={12} lg={6} xl={4}>
          <ComponentCard title="Free Trial Users (%)" isCollapsible={true}>
            <Chart
              getOptions={getDonutOptions}
              props={{
                series: freeTrialsSeries,
                labels: [
                  `Users with free trial (${get(customerStatistics, 'usersWithFreeTrial', 0)})`,
                  `Users with paid subscription (${get(customerStatistics, 'usersWithPaidSubscription', 0)})`,
                ],
              }}
              type="donut"
              height={225}
            />
          </ComponentCard>
        </Col>

        <Col xs={12} md={12} lg={6} xl={4}>
          <ComponentCard title="New Sign-ups - weekly" isCollapsible>
            <Chart
              getOptions={getColumnOptions}
              props={{
                series: weeklySignUpsSeries,
                labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
              }}
              type="bar"
              height={225}
            />
          </ComponentCard>
        </Col>
        <Col xs={12} md={12} lg={12} xl={4}>
          <ComponentCard title="New Sign-ups - monthly" isCollapsible>
            <Chart
              getOptions={getColumnOptions}
              props={{
                series: monthlySignUpsSeries,
                labels: [
                  'Jan',
                  'Feb',
                  'Mar',
                  'Apr',
                  'May',
                  'Jun',
                  'Jul',
                  'Aug',
                  'Sep',
                  'Oct',
                  'Nov',
                  'Dec',
                ],
              }}
              type="bar"
              height={225}
            />
          </ComponentCard>
        </Col>
      </Row>
      <ComponentCard title="Customer Overview" isCollapsible>
        <Row className="mt-2 dashboard__snapshot-detailed-row">
          <Col>
            <DetailedSnapshotCard
              title="Low Activity Buyers"
              value={get(customerStatistics, 'buyersWithMaxOneBoughtVehicle', 0)}
              variant="success"
              icon={<TbTrendingDown />}
              showDatePicker
              datePickerStartValue={buyersWithMaxOneBoughtVehicleRangeStart}
              datePickerEndValue={buyersWithMaxOneBoughtVehicleRangeEnd}
              setDatePickerStartValue={setBuyersWithMaxOneBoughtVehicleRangeStart}
              setDatePickerEndValue={setBuyersWithMaxOneBoughtVehicleRangeEnd}
              defaultDatePickerStartValue={today}
              defaultDatePickerEndValue={thirtyDaysAgo}
              datePickerStartPlaceholder="Last 30 days"
              showDateComparison={false}
            />
            <DetailedSnapshotCard
              title="Unsold Sellers"
              value={get(customerStatistics, 'sellersWithNoSoldVehicle', 0)}
              variant="purple"
              icon={<TbCarOff />}
              showDatePicker={false}
              footer={
                <p className="d-flex align-items-center gap-1 mb-0 mt-3 dashboard__detailed-snapshot-card-footer">
                  <span className="text-nowrap text-body-color fs-5">As of today</span>
                </p>
              }
            />
          </Col>
          <Col>
            <DetailedSnapshotCard
              title="Low Activity Sellers"
              value={get(customerStatistics, 'sellersWithMaxOneSoldVehicle', 0)}
              variant="secondary"
              icon={<TbTrendingDown />}
              showDatePicker
              datePickerStartValue={sellersWithMaxOneSoldVehicleRangeStart}
              datePickerEndValue={sellersWithMaxOneSoldVehicleRangeEnd}
              setDatePickerStartValue={setSellersWithMaxOneSoldVehicleRangeStart}
              setDatePickerEndValue={setSellersWithMaxOneSoldVehicleRangeEnd}
              defaultDatePickerStartValue={today}
              defaultDatePickerEndValue={thirtyDaysAgo}
              datePickerStartPlaceholder="Last 30 days"
              showDateComparison={false}
            />
            <DetailedSnapshotCard
              title="Users Logged In to Mobile App"
              value={get(customerStatistics, 'usersLoggedInToMobileApp', 0)}
              variant="info"
              icon={<FaMobileScreenButton />}
              footer={
                <p className="d-flex align-items-center gap-1 mb-0 mt-3 dashboard__detailed-snapshot-card-footer">
                  <span className="text-nowrap text-body-color fs-5">Now</span>
                </p>
              }
              showDateComparison={false}
            />
          </Col>
          <Col>
            <DetailedSnapshotCard
              title="Free Trial Ends In 2 Weeks"
              value={get(customerStatistics, 'endingFreeTrialInTwoWeeks', 0)}
              variant="danger"
              icon={<PiSortAscending />}
              showDatePicker={false}
              footer={
                <Col
                  onClick={() => {
                    dispatch({
                      type: SET_DEALER_SEARCH_STATE,
                      payload: {
                        searchParams: {
                          offset: 0,
                          endingFreeTrialInTwoWeeks: true,
                        },
                      },
                    });
                    navigate(RouterPaths.DEALERS);
                  }}
                >
                  <p className="d-flex align-items-center gap-1 mb-0 mt-3 dashboard__detailed-snapshot-card-footer--clickable cursor-pointer">
                    <span className="text-nowrap text-body-color fs-5">Show users</span>
                  </p>
                </Col>
              }
            />
            <DetailedSnapshotCard
              title="Users With Enabled Push Notifications"
              value={get(customerStatistics, 'usersWithEnabledPushNotifications', 0)}
              variant="success"
              icon={<IoMdNotifications />}
              footer={
                <p className="d-flex align-items-center gap-1 mb-0 mt-3 dashboard__detailed-snapshot-card-footer">
                  <span className="text-nowrap text-body-color fs-5">Now</span>
                </p>
              }
              showDateComparison={false}
            />
          </Col>
          <DetailedSnapshotCard
            title="Unpurchased Buyers"
            value={get(customerStatistics, 'buyersWithNoBoughtVehicle', 0)}
            variant="warning"
            icon={<MdOutlineCreditCardOff />}
            showDatePicker={false}
            footer={
              <p className="d-flex align-items-center gap-1 mb-0 mt-3 dashboard__detailed-snapshot-card-footer">
                <span className="text-nowrap text-body-color fs-5">As of today</span>
              </p>
            }
          />
        </Row>
        <Row />
      </ComponentCard>
      <Row>
        <Col xs={12} md={12} lg={12} xl={feesChartsAlignedHorizontally ? 4 : 6}>
          <ComponentCard
            title="Buyers & Sellers Locations"
            titleClassName="text-truncate"
            isCollapsible
            headerTrailing={
              <Col className="d-flex justify-content-center" onClick={handleGoToLocations}>
                <p className="dashboard__detailed-snapshot-card-footer--clickable m-0 cursor-pointer">
                  <span className="text-body-color fs-5 text-truncate">Show all</span>
                </p>
              </Col>
            }
          >
            <Chart
              getOptions={getHorizontalBarChartOptions}
              props={{
                series: citiesData.series,
                labels: citiesData.labels,
                eventHandler: handleCityChartBarClick,
              }}
              type="bar"
              height={225}
            />
          </ComponentCard>
        </Col>
        <Col xs={12} md={12} lg={12} xl={feesChartsAlignedHorizontally ? 4 : 6}>
          <ComponentCard title="Sales vs CAP" isCollapsible>
            <Chart
              getOptions={getMixedChartOptions}
              type="line"
              height={225}
              props={{
                series: salesVersusCapData?.series,
                labels: salesVersusCapData?.labels,
              }}
            />
          </ComponentCard>
        </Col>
        <Col xs={12} md={12} lg={12} xl={feesChartsAlignedHorizontally ? 4 : 12}>
          <ComponentCard title="Buyers and Sellers Fees" isCollapsible>
            <Chart
              getOptions={getRangeColumnCharts}
              props={{
                series: mapBuyersAndSellersFees(),
              }}
              type="bar"
              height={225}
            />
          </ComponentCard>
        </Col>
      </Row>
    </>
  );
}

export default CustomerOverviewSection;
